import type { Money } from 'shared/util/money';
import type { PriceCleaningType } from '@/domain';
import type { Payment, SettlementDetails } from '@/domain/transaction';
import { type CancellationPolicy, createCancellationPolicy } from '@/models/cancellationPolicy';
import { createItemAddress, type ItemAddress } from '@/models/itemAddress';
import type { RentalProductType } from '@/utils/rentalProductType';
import { SettlementTrigger } from '@/utils/settlementTrigger';
import { createRentalProfile, type RentalProfile } from './rentalProfile';
import { removeEmptyProperties } from './utils';

export enum RentalStatusType {
    CREATED = 'CREATED',
    CONFIRMED = 'CONFIRMED',
    CONFIRMED_WITH_AMENDMENT = 'CONFIRMED_WITH_AMENDMENT',
    REJECTED_BY_HOST = 'REJECTED_BY_HOST',
    WITHDRAWN_BY_HOST = 'WITHDRAWN_BY_HOST',
    WITHDRAWN_BY_TENANT = 'WITHDRAWN_BY_TENANT',
    CANCELLED_BY_HOST = 'CANCELLED_BY_HOST',
    CANCELLED_BY_TENANT = 'CANCELLED_BY_TENANT',
    FULL_PAYMENT_PAID = 'FULL_PAYMENT_PAID',
    UPFRONT_PAYMENT_PAID = 'UPFRONT_PAYMENT_PAID',
    REMAINING_PAYMENT_READY = 'REMAINING_PAYMENT_READY',
    REMAINING_PAYMENT_PAID = 'REMAINING_PAYMENT_PAID',
    STAY_IN_PROGRESS = 'STAY_IN_PROGRESS',
    PAYOUT_INITIATED = 'PAYOUT_INITIATED',
    PAYOUT_COMPLETED = 'PAYOUT_COMPLETED',
    STAY_COMPLETED = 'STAY_COMPLETED',
    CONFIRMATION_EXPIRED = 'CONFIRMATION_EXPIRED',
    UPFRONT_PAYMENT_EXPIRED = 'UPFRONT_PAYMENT_EXPIRED',
    REMAINING_PAYMENT_EXPIRED = 'REMAINING_PAYMENT_EXPIRED',
    FULL_PAYMENT_EXPIRED = 'FULL_PAYMENT_EXPIRED',
    REFUNDED = 'REFUNDED',
    SETTLED_WITH_REFUND = 'SETTLED_WITH_REFUND',
}

export interface RentalProduct {
    type: RentalProductType;
    amount: Money;
    amendment: boolean;
    note: string | null;
}

export interface Dispute {
    isSolved: boolean;
    isCommitted: boolean;
    createdAt: string;
    solvedAt?: string;
}

const createDispute = (data: Record<string, unknown>): Dispute => {
    return {
        isSolved: data.isSolved ? Boolean(data.isSolved) : false,
        isCommitted: data.isCommitted ? Boolean(data.isCommitted) : false,
        createdAt: data.createdAt ? String(data.createdAt) : '',
        solvedAt: data.solvedAt ? String(data.solvedAt) : '',
    };
};

const createSettlementDetails = (data: Record<string, unknown>): SettlementDetails => {
    const required = ['host', 'tenant', 'serviceProvider', 'trigger', 'triggerTime'];
    for (const fieldName of required) {
        if (!(Object.hasOwn(data, fieldName) && typeof data[fieldName] !== 'undefined')) {
            throw new Error(`createSettlementDetails: "${fieldName}" is required`);
        }
    }
    const host = data.host as Record<string, unknown>;
    const tenant = data.tenant as Record<string, unknown>;
    const serviceProvider = data.serviceProvider as Record<string, unknown>;

    return {
        host: {
            value: Number(host.value),
            currency: String(host.currency),
        },
        tenant: {
            value: Number(tenant.value),
            currency: String(tenant.currency),
        },
        serviceProvider: {
            value: Number(serviceProvider.value),
            currency: String(serviceProvider.currency),
        },
        trigger: SettlementTrigger[data.trigger as keyof typeof SettlementTrigger],
        triggerTime: String(data.triggerTime),
    };
};

// Should match RentalStatusResponse in fhh-transaction StatusController
export interface RentalStatus {
    bookingReference: string;
    endDate: string;
    guests: number;
    itemDescription?: string;
    itemId: number;
    itemImage?: string;
    itemTitle?: string;
    itemAddress?: ItemAddress;
    messageFromTenant?: string;
    payments: Payment[];
    products: RentalProduct[];
    profile: RentalProfile;
    cleaningType?: PriceCleaningType;
    settlementDetails: SettlementDetails | null;
    startDate: string;
    status: RentalStatusType[];
    currentDeadline?: string;
    dispute?: Dispute;
    cancellationPolicy: CancellationPolicy;
    termsAndConditionsUrl: string;
}
//These fields are required in the api-response, it serves to validate that the api-response contains the expected fields, not the values they have.
const requiredFields = [
    'bookingReference',
    'endDate',
    'guests',
    'itemId',
    'payments',
    'products',
    'profile',
    'startDate',
    'status',
    'cancellationPolicy',
    'termsAndConditionsUrl',
];

export const createRentalStatus = (data: Record<string, unknown>): RentalStatus => {
    const rentalStatus: Partial<RentalStatus> = {
        // Optional fields
        itemTitle: data.itemTitle ? String(data.itemTitle) : undefined,
        itemDescription: data.itemDescription ? String(data.itemDescription) : undefined,
        itemImage: data.itemImage ? String(data.itemImage) : undefined,
        itemAddress: data.itemAddress ? createItemAddress({ ...data.itemAddress }) : undefined,
        messageFromTenant: data.messageFromTenant ? String(data.messageFromTenant) : undefined,
        currentDeadline: data.currentDeadline ? String(data.currentDeadline) : undefined,
        profile: data.profile ? createRentalProfile({ ...data.profile }) : undefined,
        dispute: data.dispute ? createDispute({ ...data.dispute }) : undefined,
        cleaningType: data.cleaningType ? (data.cleaningType as PriceCleaningType) : undefined,
        settlementDetails: data.settlementDetails ? createSettlementDetails({ ...data.settlementDetails }) : null,
        cancellationPolicy: data.cancellationPolicy ? createCancellationPolicy({ ...data.cancellationPolicy }) : undefined,
    };

    for (const fieldName of requiredFields) {
        if (!(Object.hasOwn(data, fieldName) && typeof data[fieldName] !== 'undefined')) {
            throw new Error(`createRentalStatus: "${fieldName}" is required`);
        }

        rentalStatus[fieldName] = data[fieldName];
    }

    removeEmptyProperties(rentalStatus);

    return {
        ...(rentalStatus as RentalStatus),
    };
};

export const createRentalStatusState = (data: Record<string, unknown>): { status: RentalStatusType } => {
    if (!Object.values(RentalStatusType).includes(data.status as RentalStatusType)) {
        throw new Error(`createRentalStatusState: Unknown status "${data.status}"`);
    }

    return {
        status: data.status as RentalStatusType,
    };
};

export const showContactInfo = [
    RentalStatusType.UPFRONT_PAYMENT_PAID,
    RentalStatusType.REMAINING_PAYMENT_READY,
    RentalStatusType.REMAINING_PAYMENT_PAID,
    RentalStatusType.FULL_PAYMENT_PAID,
    RentalStatusType.STAY_IN_PROGRESS,
    RentalStatusType.PAYOUT_INITIATED,
    RentalStatusType.PAYOUT_COMPLETED,
    RentalStatusType.STAY_COMPLETED,
    RentalStatusType.SETTLED_WITH_REFUND,
    RentalStatusType.CANCELLED_BY_HOST,
    RentalStatusType.CANCELLED_BY_TENANT,
    RentalStatusType.REMAINING_PAYMENT_EXPIRED,
];

export const enableInsuranceClaim = [
    RentalStatusType.STAY_IN_PROGRESS,
    RentalStatusType.PAYOUT_INITIATED,
    RentalStatusType.PAYOUT_COMPLETED,
    RentalStatusType.STAY_COMPLETED,
    RentalStatusType.SETTLED_WITH_REFUND,
];

export type PaymentOption = 'CREDIT_CARD' | 'VIPPS' | 'TRUSTLY';
